import React from "react";
import { motion } from "framer-motion";
import * as Pi from "@phosphor-icons/react";
import { ArrowRight, Check, PlayCircle, Star } from "@phosphor-icons/react";
import { CONTENT, IMAGES } from "../../data/content";
import { useLang } from "../../context/LanguageContext";
import { scrollToId } from "../../lib/api";
import { Reveal, SectionLabel } from "./Motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";

/* ---------------- Solutions (after hero) ---------------- */
export const Solutions = () => {
  const { lang } = useLang();
  const t = CONTENT[lang].solutions;
  return (
    <section id="soluzioni" data-testid="solutions-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white max-w-3xl">{t.title}</h2>
          <p className="mt-5 text-lg text-zinc-400 max-w-2xl">{t.subtitle}</p>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.items.map((s, i) => {
            const Icon = Pi[s.icon] || Pi.Cube;
            return (
              <Reveal key={s.t} delay={i * 0.07}>
                <motion.div whileHover={{ y: -8 }} data-testid={`solution-card-${i}`}
                  className="group h-full rounded-3xl border border-white/10 bg-[#0A0A0A] p-7 flex flex-col hover:border-blue-500/50 transition-colors duration-300 relative overflow-hidden">
                  <div className="absolute -right-12 -top-12 w-32 h-32 bg-blue-500/0 group-hover:bg-blue-500/15 blur-3xl transition-colors duration-500" />
                  <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/25 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={26} weight="duotone" className="text-blue-400" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white">{s.t}</h3>
                  <p className="mt-2 text-sm text-zinc-500 leading-relaxed flex-1">{s.d}</p>
                  <button data-testid={`solution-discover-${i}`} onClick={() => scrollToId(s.target)}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-blue-400 transition-colors">
                    {t.discover} <ArrowRight size={15} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Marquee ---------------- */
export const Marquee = () => {
  const { lang } = useLang();
  const items = CONTENT[lang].marquee;
  const row = [...items, ...items];
  return (
    <section data-testid="marquee" className="py-12 border-y border-white/10 overflow-hidden bg-[#0A0A0A]">
      <div className="flex whitespace-nowrap animate-marquee">
        {row.map((it, i) => (
          <span key={i} className="font-heading font-black text-5xl md:text-7xl text-stroke mx-8 uppercase tracking-tight">
            {it} <span className="text-blue-500/40">/</span>
          </span>
        ))}
      </div>
    </section>
  );
};

/* ---------------- App Showcase ---------------- */
export const AppShowcase = ({ settings }) => {
  const { lang } = useLang();
  const t = CONTENT[lang].app;
  return (
    <section id="app" data-testid="app-section" className="relative py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel></Reveal>
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Reveal>
              <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight leading-[1.05] text-white">{t.title}</h2>
              <p className="mt-5 text-lg text-zinc-400">{t.subtitle}</p>
              <p className="mt-4 text-base text-zinc-500 leading-relaxed">{t.intro}</p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
                {t.features.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 text-sm text-zinc-300">
                    <Check size={16} weight="bold" className="text-blue-500 shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 rounded-2xl border border-white/10 bg-[#0A0A0A] p-6">
                <div className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-4">{t.idealTitle}</div>
                <div className="flex flex-wrap gap-2">
                  {t.ideal.map((i) => (
                    <span key={i} className="text-sm text-zinc-300 border border-white/10 rounded-full px-3 py-1.5 hover:border-blue-500/50 transition-colors">{i}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right: mockup + pricing */}
          <div className="lg:sticky lg:top-28 space-y-8">
            <Reveal delay={0.1}>
              <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 200 }}
                className="relative rounded-3xl overflow-hidden border border-white/10 group">
                <div className="absolute -inset-10 bg-blue-500/20 blur-[80px] -z-10" />
                <img src={IMAGES.appMockup} alt="Anteprima app di prenotazione AppForge Studio" loading="lazy" decoding="async" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                <button data-testid="app-demo-btn" onClick={() => scrollToId("contatti")}
                  className="absolute bottom-6 left-6 inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold px-5 py-3 hover:bg-blue-500 hover:text-white transition-colors">
                  <PlayCircle size={20} weight="fill" /> {t.demoCta}
                </button>
              </motion.div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-blue-500/30 bg-gradient-to-b from-blue-500/10 to-transparent p-8">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-400">{t.priceLabel}</div>
                <div className="flex items-end gap-2 mt-2">
                  <span className="font-heading font-black text-6xl text-white">€{settings.app_price_oneoff}</span>
                  <span className="text-zinc-500 mb-2">{t.priceNote}</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2">
                  <span className="font-heading font-bold text-xl text-blue-400">€{settings.app_price_monthly}</span>
                  <span className="text-xs text-zinc-400">/ {lang === "it" ? "mese" : "month"} — {t.monthlyNote}</span>
                </div>
                <ul className="mt-5 space-y-2">
                  {t.priceExplain.map((e) => (
                    <li key={e} className="flex gap-2 text-sm text-zinc-400"><Check size={16} className="text-blue-500 shrink-0 mt-0.5" />{e}</li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-col gap-3">
                  <button data-testid="app-cta1" onClick={() => scrollToId("contatti")}
                    className="w-full rounded-full bg-blue-500 text-white font-semibold py-3.5 hover:bg-blue-400 transition-colors">{t.cta1}</button>
                  <button data-testid="app-cta2" onClick={() => scrollToId("contatti")}
                    className="w-full rounded-full border border-white/20 text-white font-semibold py-3.5 hover:bg-white/5 transition-colors">{t.cta2}</button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Landing Page ---------------- */
export const LandingSection = ({ settings }) => {
  const { lang } = useLang();
  const t = CONTENT[lang].landing;
  return (
    <section id="landing" data-testid="landing-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="order-2 lg:order-1">
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <div className="absolute -inset-8 bg-blue-500/10 blur-[70px] -z-10" />
            <img src={IMAGES.softwareDev} alt="Landing page professionale AppForge Studio" loading="lazy" decoding="async" className="w-full h-[380px] object-cover" />
          </div>
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal><SectionLabel>{t.label}</SectionLabel>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">{t.title}</h2>
            <p className="mt-5 text-lg text-zinc-400">{t.subtitle}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 flex flex-wrap gap-2">
              {t.useCases.map((u) => (
                <span key={u} className="text-xs text-zinc-400 border border-white/10 rounded-full px-3 py-1.5">{u}</span>
              ))}
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {t.benefits.map((b) => (
                <div key={b} className="flex items-center gap-2 text-sm text-zinc-300"><Check size={15} weight="bold" className="text-blue-500 shrink-0" />{b}</div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex items-end gap-2">
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{t.priceLabel}</span>
              <span className="font-heading font-black text-5xl text-white">€{settings.landing_price}</span>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button data-testid="landing-cta1" onClick={() => scrollToId("contatti")}
                className="rounded-full bg-white text-black font-semibold px-6 py-3.5 hover:bg-blue-500 hover:text-white transition-colors">{t.cta1}</button>
              <button data-testid="landing-cta2" onClick={() => scrollToId("portfolio")}
                className="rounded-full border border-white/20 text-white font-semibold px-6 py-3.5 hover:bg-white/5 transition-colors">{t.cta2}</button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Services ---------------- */
export const Services = () => {
  const { lang } = useLang();
  const t = CONTENT[lang].services;
  return (
    <section id="servizi" data-testid="services-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white max-w-3xl">{t.title}</h2>
          <p className="mt-5 text-lg text-zinc-400 max-w-2xl">{t.subtitle}</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.items.map((s, i) => {
            const Icon = Pi[s.icon] || Pi.Cube;
            return (
              <Reveal key={s.t} delay={(i % 4) * 0.05}>
                <motion.div whileHover={{ y: -5 }} data-testid={`service-card-${i}`}
                  className="group h-full rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 hover:border-blue-500/40 transition-colors duration-300 relative overflow-hidden">
                  <div className="absolute -right-10 -top-10 w-24 h-24 bg-blue-500/0 group-hover:bg-blue-500/10 blur-2xl transition-colors duration-500" />
                  <Icon size={30} weight="duotone" className="text-blue-400 mb-4" />
                  <h3 className="font-heading font-bold text-lg text-white">{s.t}</h3>
                  <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{s.d}</p>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-10 rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 md:p-8 text-zinc-400 text-sm md:text-base leading-relaxed">{t.note}</div>
          <div className="mt-10 rounded-3xl border border-blue-500/30 bg-gradient-to-r from-blue-500/10 to-transparent p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <h3 className="font-heading font-bold text-2xl md:text-3xl text-white max-w-xl">{t.cta}</h3>
            <button data-testid="services-cta" onClick={() => scrollToId("contatti")}
              className="group inline-flex items-center gap-2 rounded-full bg-blue-500 text-white font-semibold px-7 py-4 hover:bg-blue-400 transition-colors whitespace-nowrap">
              {CONTENT[lang].ctaConsult} <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Portfolio ---------------- */
export const Portfolio = ({ items = [] }) => {
  const { lang } = useLang();
  const t = CONTENT[lang].portfolio;
  const fallback = IMAGES.appMockup;
  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">{t.title}</h2>
          <p className="mt-5 text-lg text-zinc-400 max-w-2xl">{t.subtitle}</p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p, i) => {
            const title = lang === "it" ? p.title_it : p.title_en;
            const desc = lang === "it" ? p.desc_it : p.desc_en;
            return (
              <Reveal key={p.id || i} delay={i * 0.08}>
                <motion.div whileHover={{ y: -6 }} data-testid={`portfolio-card-${i}`}
                  className={`group rounded-3xl overflow-hidden border ${p.featured ? "border-blue-500/40" : "border-white/10"} bg-[#050505]`}>
                  <div className="relative h-56 overflow-hidden">
                    <img src={p.image_url || fallback} alt={title} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${!p.featured ? "opacity-40 grayscale" : ""}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
                    {p.featured && <span className="absolute top-4 left-4 text-xs font-mono uppercase tracking-widest bg-blue-500 text-white px-3 py-1 rounded-full">Demo</span>}
                  </div>
                  <div className="p-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-blue-400">{p.category}</div>
                    <h3 className="mt-2 font-heading font-bold text-xl text-white">{title}</h3>
                    <p className="mt-2 text-sm text-zinc-500">{desc}</p>
                    {p.featured && (
                      <button data-testid="portfolio-open-demo" onClick={() => scrollToId("contatti")}
                        className="mt-4 inline-flex items-center gap-2 text-sm text-white hover:text-blue-400 transition-colors">
                        {t.viewCase} <ArrowRight size={15} weight="bold" />
                      </button>
                    )}
                  </div>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Why Us ---------------- */
export const WhyUs = () => {
  const { lang } = useLang();
  const t = CONTENT[lang].why;
  return (
    <section data-testid="why-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white max-w-2xl">{t.title}</h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
          {t.items.map((w, i) => (
            <Reveal key={w.t} delay={(i % 4) * 0.05}>
              <div data-testid={`why-card-${i}`} className="h-full bg-[#0A0A0A] p-7 hover:bg-[#101010] transition-colors">
                <span className="font-mono text-blue-500 text-sm">0{i + 1}</span>
                <h3 className="mt-3 font-heading font-bold text-lg text-white">{w.t}</h3>
                <p className="mt-2 text-sm text-zinc-500">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="why-badges">
            {CONTENT[lang].whyBadges.map((b, i) => (
              <span key={b} data-testid={`why-badge-${i}`}
                className="inline-flex items-center gap-2 text-sm text-zinc-300 border border-white/10 bg-[#0A0A0A] rounded-full px-4 py-2 hover:border-blue-500/50 hover:text-white transition-colors">
                <Check size={15} weight="bold" className="text-blue-500" /> {b}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Help (Come possiamo aiutarti) ---------------- */
export const HelpSection = () => {
  const { lang } = useLang();
  const t = CONTENT[lang].help;
  return (
    <section id="aiuto" data-testid="help-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">{t.title}</h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {t.items.map((h, i) => {
            const Icon = Pi[h.icon] || Pi.Cube;
            return (
              <Reveal key={h.t} delay={i * 0.08}>
                <motion.div whileHover={{ y: -6 }} data-testid={`help-card-${i}`}
                  className="group h-full rounded-3xl border border-white/10 bg-[#050505] p-8 flex flex-col hover:border-blue-500/50 transition-colors duration-300">
                  <Icon size={34} weight="duotone" className="text-blue-400 mb-5" />
                  <h3 className="font-heading font-bold text-xl text-white">{h.t}</h3>
                  <p className="mt-2 text-sm text-zinc-500 leading-relaxed flex-1">{h.d}</p>
                  <button data-testid={`help-cta-${i}`} onClick={() => scrollToId("contatti")}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-semibold py-3 hover:bg-blue-500 hover:border-blue-500 transition-colors">
                    {t.cta} <ArrowRight size={16} weight="bold" />
                  </button>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Final CTA ---------------- */
export const FinalCta = () => {
  const { lang } = useLang();
  const t = CONTENT[lang].finalCta;
  return (
    <section data-testid="final-cta-section" className="px-6 md:px-12 py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <div className="relative rounded-[2.5rem] border border-blue-500/30 bg-[#0A0A0A] overflow-hidden px-8 md:px-16 py-16 md:py-24 text-center">
            <div className="absolute inset-0 grid-bg opacity-40" />
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[60%] h-64 bg-blue-500/20 blur-[120px]" />
            <div className="relative">
              <h2 className="font-heading font-black text-4xl md:text-6xl tracking-tighter text-white max-w-3xl mx-auto leading-[0.95]">{t.title}</h2>
              <p className="mt-6 text-lg text-zinc-400 max-w-2xl mx-auto">{t.subtitle}</p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                <button data-testid="final-cta1" onClick={() => scrollToId("contatti")}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 text-white font-semibold px-8 py-4 hover:bg-blue-400 transition-colors">
                  {t.cta1} <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button data-testid="final-cta2" onClick={() => scrollToId("contatti")}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-semibold px-8 py-4 hover:bg-white/5 transition-colors">
                  {t.cta2}
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Process ---------------- */
export const Process = () => {
  const { lang } = useLang();
  const t = CONTENT[lang].process;
  return (
    <section id="processo" data-testid="process-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">{t.title}</h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.steps.map((s, i) => (
            <Reveal key={s.t} delay={(i % 4) * 0.06}>
              <div data-testid={`process-step-${i}`} className="relative border-t border-white/15 pt-5">
                <div className="font-heading font-black text-6xl text-white/10">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-3 font-heading font-bold text-lg text-white">{s.t}</h3>
                <p className="mt-2 text-sm text-zinc-500">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Pricing ---------------- */
export const Pricing = ({ settings }) => {
  const { lang } = useLang();
  const t = CONTENT[lang].pricing;
  const priceFor = (key) => (key === "app" ? settings.app_price_oneoff : settings.landing_price);
  return (
    <section id="prezzi" data-testid="pricing-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">{t.title}</h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {t.plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <div data-testid={`pricing-plan-${i}`} className={`h-full rounded-3xl border p-8 flex flex-col ${p.highlight ? "border-blue-500/50 bg-gradient-to-b from-blue-500/10 to-transparent" : "border-white/10 bg-[#0A0A0A]"}`}>
                {p.highlight && <span className="self-start text-xs font-mono uppercase tracking-widest bg-blue-500 text-white px-3 py-1 rounded-full mb-4">Popular</span>}
                <h3 className="font-heading font-bold text-xl text-white">{p.name}</h3>
                <div className="mt-4">
                  {p.custom ? (
                    <span className="font-heading font-black text-3xl text-white">{t.customLabel}</span>
                  ) : (
                    <div className="flex items-end gap-2">
                      <span className="text-sm text-zinc-500 mb-2">{p.suffix}</span>
                      <span className="font-heading font-black text-5xl text-white">€{priceFor(p.priceKey)}</span>
                    </div>
                  )}
                  {p.monthly && <div className="mt-1 text-sm text-blue-400">+ €{settings.app_price_monthly}/{lang === "it" ? "mese" : "month"}</div>}
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm text-zinc-300"><Check size={16} weight="bold" className="text-blue-500 shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <button data-testid={`pricing-cta-${i}`} onClick={() => scrollToId("contatti")}
                  className={`mt-7 w-full rounded-full font-semibold py-3.5 transition-colors ${p.highlight ? "bg-blue-500 text-white hover:bg-blue-400" : "border border-white/20 text-white hover:bg-white/5"}`}>{t.cta}</button>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}><p className="mt-8 text-sm text-zinc-500 max-w-2xl">{t.note}</p></Reveal>
      </div>
    </section>
  );
};

/* ---------------- Testimonials ---------------- */
export const Testimonials = ({ items = [] }) => {
  const { lang } = useLang();
  const t = CONTENT[lang].testimonials;
  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 px-6 md:px-12 bg-[#0A0A0A] border-y border-white/10">
      <div className="max-w-[1400px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">{t.title}</h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {items.map((tst, i) => {
            const role = lang === "it" ? tst.role_it : tst.role_en;
            const text = lang === "it" ? tst.text_it : tst.text_en;
            return (
              <Reveal key={tst.id || i} delay={i * 0.08}>
                <div data-testid={`testimonial-${i}`} className="h-full rounded-3xl border border-white/10 bg-[#050505] p-8 flex flex-col">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, s) => <Star key={s} size={16} weight="fill" className="text-blue-500" />)}
                  </div>
                  <p className="text-zinc-300 leading-relaxed flex-1">"{text}"</p>
                  <div className="mt-6 flex items-center gap-3">
                    <img src={tst.image_url || IMAGES.testimonialM} alt={`Foto di ${tst.name}`} loading="lazy" decoding="async" className="w-11 h-11 rounded-full object-cover grayscale" />
                    <div>
                      <div className="font-semibold text-white text-sm">{tst.name}</div>
                      <div className="text-xs text-zinc-500">{role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- FAQ ---------------- */
export const FAQ = ({ items = [] }) => {
  const { lang } = useLang();
  const t = CONTENT[lang].faq;
  return (
    <section id="faq" data-testid="faq-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[900px] mx-auto">
        <Reveal><SectionLabel>{t.label}</SectionLabel>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">{t.title}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-10 w-full">
            {items.map((f, i) => (
              <AccordionItem key={f.id || i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left text-white hover:text-blue-400 text-base md:text-lg font-medium py-5">{lang === "it" ? f.q_it : f.q_en}</AccordionTrigger>
                <AccordionContent className="text-zinc-400 text-base leading-relaxed pb-5">{lang === "it" ? f.a_it : f.a_en}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};
